import { functionalUpdate, SortingState } from '@tanstack/react-table';
import { VirtualizedTable, tableHelpers } from '@round/creatorbase-ui';
import { CreatorTableEmptyState } from 'Modules/Plans/CreatorSearch/components/CreatorExploreTable/EmptyState/CreatorTableEmptyState';
import { influencer } from '@round/api';
import { CreatorCell } from 'Modules/Plans/CreatorSearch/components/CreatorExploreTable/cells/CreatorCell/CreatorCell';
import { LocationCell } from 'Modules/Plans/CreatorSearch/components/CreatorExploreTable/cells/LocationCell/LocationCell';
import { ResponseRateCell } from 'Modules/Plans/CreatorSearch/components/CreatorExploreTable/cells/ResponseRateCell/ResponseRateCell';
import { TagsCell } from 'Modules/Plans/CreatorSearch/components/CreatorExploreTable/cells/TagsCell/TagsCell';
import { PlatformSelectorCell } from 'Modules/Plans/CreatorSearch/components/CreatorExploreTable/cells/PlatformSelectorCell/PlatformSelectorCell';
import {
    AvgCpeCell,
    FollowersCell,
    CostCell,
    PlaysCell,
    CpmCell,
    EnagementRateCell,
    PostingFrequencyCell,
} from 'Modules/Plans/CreatorSearch/components/CreatorExploreTable/cells/PlatformStatCells/PlatformStatCells';
import { getAverageDraftTimeLabel, getLastCollabLabel } from 'Modules/Plans/CreatorSearch/utils/creatorSearchUtils';
import {
    PAGE_SIZE,
    Column,
    DATE_ADDED_COLUMN_ID,
    getDateAddedValue,
} from 'Modules/Plans/CreatorSearch/components/CreatorExploreTable/helpers';
import { AddPostCell } from './components/AddPostCell/AddPostCell';
import styles from './AddToCampaignTable.module.css';
import { Meta } from './helpers';
import cn from 'classnames';

const SkeletonCell = tableHelpers.SkeletonCell;

const PLATFORM_STAT_COLUMN_SIZE = 115;

type Props = {
    data: influencer.CreatorSearchResult[];
    meta: Meta;
    sorting: SortingState;
    onSortingChange: (sorting: SortingState) => void;
    fetchNextPage: () => void;
    onRowClick?: (creator: influencer.CreatorSearchResult) => void;
    className?: string;
};

const AddToCampaignTable = ({ data, meta, sorting, onSortingChange, fetchNextPage, onRowClick, className }: Props) => {
    const columns: Column[] = [
        {
            header: 'Creator',
            id: 'creator',
            cell: CreatorCell,
            size: 200,
            enableSorting: false,
        },
        {
            header: 'Location',
            id: 'location',
            cell: LocationCell,
            enableSorting: false,
        },
        {
            header: 'Tags',
            id: 'tags',
            cell: TagsCell,
            enableSorting: false,
            size: 200,
        },
        {
            header: 'Draft Time',
            id: 'metrics.avg_draft_time_in_hours',
            meta: { className: styles.muted },
            accessorFn: (row) => getAverageDraftTimeLabel(row.metrics?.avg_draft_time_in_hours),
            cell: SkeletonCell,
            size: 110,
        },
        {
            header: 'Response',
            id: 'metrics.response_rate',
            cell: ResponseRateCell,
            size: 110,
        },
        {
            header: 'Last Collab',
            id: 'post_metrics.most_recent_post_date',
            meta: { className: styles.muted },
            accessorFn: (row) => getLastCollabLabel(row?.post_metrics?.most_recent_post_date),
            cell: SkeletonCell,
            size: 110,
        },
        {
            header: 'Platform',
            id: 'platform',
            meta: { className: styles.platformSelectorCell },
            cell: PlatformSelectorCell,
            enableSorting: false,
            size: 145,
        },
        {
            header: 'Avg. CPE',
            id: 'estimated_cpe',
            cell: AvgCpeCell,
            size: PLATFORM_STAT_COLUMN_SIZE,
        },
        {
            header: 'Followers',
            id: 'total_follower_count',
            cell: FollowersCell,
            size: PLATFORM_STAT_COLUMN_SIZE,
        },
        {
            header: 'Cost',
            id: 'cost',
            cell: CostCell,
            size: PLATFORM_STAT_COLUMN_SIZE,
        },
        {
            header: 'Plays',
            id: 'avg_video_views_per_post',
            cell: PlaysCell,
            size: PLATFORM_STAT_COLUMN_SIZE,
        },
        {
            header: 'CPM',
            id: 'estimated_cpm',
            cell: CpmCell,
            size: PLATFORM_STAT_COLUMN_SIZE,
        },
        {
            header: 'ER%',
            id: 'avg_engagement_rate',
            cell: EnagementRateCell,
            size: PLATFORM_STAT_COLUMN_SIZE,
        },
        {
            header: 'Posting Freq',
            id: 'post_metrics.activity_rate',
            size: PLATFORM_STAT_COLUMN_SIZE,
            cell: PostingFrequencyCell,
        },
        {
            header: 'Date Added',
            id: DATE_ADDED_COLUMN_ID,
            accessorFn: getDateAddedValue,
            cell: SkeletonCell,
            meta: {
                className: cn(styles.dateAddedCell, styles.muted),
            },
        },
        {
            header: undefined,
            id: 'actions',
            meta: { className: styles.actionsCell },
            cell: AddPostCell,
            enableSorting: false,
            size: 200,
        },
    ];

    return (
        <VirtualizedTable
            columns={columns}
            data={tableHelpers.getLoadingRows(data, { pageSize: PAGE_SIZE, isLoading: meta.isLoading })}
            meta={meta}
            virtualizerOptions={{
                estimateSize: () => 50,
                getItemKey: (index) => `${index}-${data[index]?._id}` || `skeleton-${index}`,
                overscan: 50,
            }}
            onEndReached={fetchNextPage}
            containerClassName={className}
            // The full column set is wider than a laptop screen, so keep the creator visible while
            // scrolling across to the stats and the add controls.
            columnPinning={{ left: ['creator'] }}
            enableHiding
            enableSorting
            manualSorting
            sorting={sorting}
            onSortingChange={(newSorting) => onSortingChange(functionalUpdate(newSorting, sorting))}
            onRowClick={
                onRowClick
                    ? (row) => {
                          if (!tableHelpers.isLoadingRow(row.original)) {
                              onRowClick(row.original);
                          }
                      }
                    : undefined
            }
            noDataLabel={<CreatorTableEmptyState hasError={meta.hasError} />}
        />
    );
};

export default AddToCampaignTable;
